import { safeLocalStorage } from '@/lib/storage/safe-storage';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';
import { isWebsiteBrowser } from '@/lib/website-auth/browser';

const ANON_RESOURCE_KEY = 'bazak-anon-resource-id';

let memoizedAnonId: string | null = null;

/**
 * The store is the browser's copy of record, so tabs converge on one id. Mastra binds a single
 * browser-wide cookie to the resource it last saw, so two tabs holding different ids would flip
 * that binding back and forth and each fail the other's ownership check — worse than the one
 * refusal a tab takes when it adopts a sibling's id after a simultaneous first mint.
 *
 * The memo only covers a store that cannot be read or written (private mode, quota, a privacy
 * tool wiping the entry): minting a second id mid-page is what the ownership check refuses on the
 * very next message.
 *
 * Browser-only: on a server the store no-ops, so the memo would become one id shared by every
 * request for the life of the process — the shared-owner bug this exists to prevent.
 */
function anonymousResourceId(): string {
  const stored = safeLocalStorage.getItem(ANON_RESOURCE_KEY);
  if (stored) {
    memoizedAnonId = stored;
    return stored;
  }
  memoizedAnonId ??= `anon-${safeRandomUUID()}`;
  safeLocalStorage.setItem(ANON_RESOURCE_KEY, memoizedAnonId);
  return memoizedAnonId;
}

/**
 * The client resource id for chat memory / thread history. Website-auth chat prefixes it with
 * `website-` for routing, and the website BFF swaps in a cookie-derived resource.
 *
 * Never a value shared between people: the server owns each thread by this id and refuses a
 * mismatch, so one constant standing in for every analytics-less visitor collides across them.
 */
export function resolveResourceId(distinctId: string | null | undefined): string {
  const id = distinctId || anonymousResourceId();
  return isWebsiteBrowser() ? `website-${id}` : id;
}
